/* eslint-disable @next/next/no-img-element */
import { Appointment, SUBJECT_KEYS } from "@elixir/types";
import { Select, Popover } from "antd";
import { ReactComponent as HouseChimenyRegularRegularIcon } from "../../assets/icons/font-awesome/house-chimney-medical-regular.svg";
import getBridges from "../../utils/bridges/get-bridges";
import "../../../../dr/src/index.css";
import { TooltipComponent } from "ui";
import HprHfrFlowPitchCard from "../hpr-hfr-onboarding/persuasions/dhis-pitch-card";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import { MyAccessibleClinic } from "../../utils/bridges/types/doctorProfile";
import { isDoctorOnly } from "../../utils/bridges/methods";
import { getAclViewClassName } from "@elixir/utils";
import { useRef } from "react";

const ClinicSelector = ({
  selectedClinicId,
  onClinicChange,
  showAllClinicsOption = false,
}: {
  selectedClinicId?: string;
  onClinicChange: (id: Appointment["clinic_id"]) => void;
  showAllClinicsOption?: boolean;
}) => {
  const { data: myAccessProfile } = useMyAccessProfileV2();
  let clinics = myAccessProfile?.clinics || [];

  if (clinics.length > 1 && showAllClinicsOption) {
    clinics = [{ id: "all", name: "All Clinics" }, ...clinics];
  }

  const handleChangeClinic = (id: string) => {
    getBridges()?.changeClinic(id);

    onClinicChange?.(id);
  };

  const selectRef = useRef<any>(null);

  // Handle dropdown visibility change
  const onDropdownVisibleChange = () => {
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  return (
    <>
      <Select
        key={selectedClinicId}
        ref={selectRef}
        showSearch
        placeholder="Select a clinic"
        optionFilterProp="children"
        value={selectedClinicId}
        onChange={handleChangeClinic}
        filterOption={(input, option) => {
          const childrenText =
            option?.props?.children?.props?.children?.[1]?.props?.clinic
              ?.name ?? "";
          const isMatch = childrenText
            .toLowerCase()
            .includes(input.toLowerCase());
          return isMatch;
        }}
        notFoundContent={null}
        dropdownStyle={{ minWidth: "fit-content" }}
        onDropdownVisibleChange={onDropdownVisibleChange}
        className="ant-selector-transparent dr-h-36 dr-flex dr-items-center dr-justify-between BodyRegular dr-px-12 dr-py-8 dr-rounded-8 dr-cursor-pointer dr-w-200 dr-max-w-200 dr-p-3"
      >
        {" "}
        {clinics.map((clinic) => (
          <Select.Option key={clinic.id} value={clinic.id}>
            <div className="dr-flex dr-justify-between dr-items-center dr-space-x-8 BodyBody2Regular dr-py-4 ">
              <div className="dr-flex dr-items-center dr-space-x-8">
                <HouseChimenyRegularRegularIcon className="dr-min-w-16 dr-w-16 dr-h-16" />
                <div className="dr-capitalize clinic-selector-truncate">
                  {clinic.name}
                </div>
              </div>
              {clinic.id !== "all" && <ClinicIcon clinic={clinic} />}
            </div>
          </Select.Option>
        ))}
      </Select>
    </>
  );
};

function ClinicIcon({ clinic }: { clinic: MyAccessibleClinic }) {
  if (clinic?.abdm?.hfr_id) {
    return (
      <TooltipComponent title="ABDM Compliant" placement="right">
        <img
          alt="dhis"
          src="https://cdn.eka.care/vagus/clr7tg7jk00010tgfak6fa9y6.png"
          className={`dr-w-24 dr-h-24 ${getAclViewClassName(SUBJECT_KEYS.ABHA)}`}
          height={"24"}
          width={"24"}
        />
      </TooltipComponent>
    );
  }

  if (isDoctorOnly()) {
    return (
      <Popover
        placement="bottom"
        trigger="hover"
        className="dr-cursor-pointer"
        content={<HprHfrFlowPitchCard clinicId={clinic?.id} />}
        destroyTooltipOnHide={true}
        overlayClassName="popover-p-0"
        zIndex={1050}
      >
        <img
          alt="dhis"
          src="https://cdn.eka.care/vagus/clr7tg7jk00010tgfak6fa9y6.png"
          className={`dr-w-24 dr-h-24 dr-grayscale ${getAclViewClassName(SUBJECT_KEYS.ABHA)}`}
          height={"24"}
          width={"24"}
        />
      </Popover>
    );
  }

  return (
    <TooltipComponent title="Not ABDM Compliant" placement="right">
      <img
        alt="dhis"
        src="https://cdn.eka.care/vagus/clr7tg7jk00010tgfak6fa9y6.png"
        className={`dr-w-24 dr-h-24 dr-grayscale ${getAclViewClassName(SUBJECT_KEYS.ABHA)}`}
        height={"24"}
        width={"24"}
      />
    </TooltipComponent>
  );
}

export default ClinicSelector;
